/* import __COLOCATED_TEMPLATE__ from './edit-expenses-modal.hbs'; */
import { Promise } from 'rsvp';
import { computed, set } from '@ember/object';
import { A } from '@ember/array';
import { inject as service } from '@ember/service';
import LSGModalBase from "../lsg/lsg-modal-dialog-base";
import { closeModal } from 'sh-www/utils/modal-controls';
import { handleErrorResponseWithModal, setErrors } from 'sh-www/utils/handles-errors';
import { PROJECT_LINE_ITEM } from "../../../models/project-line-item";
import { task } from 'ember-concurrency';
import { APPLICATION } from "../../../models/application";
import { readOnly } from '@ember/object/computed';

const CLASS_NAME = 'SH-c-edit-expenses-modal';

export default LSGModalBase.extend({
  className: CLASS_NAME,

  /**
   * Register services
   */
  applicationCommandService : service('commands/application-commands'),
  store: service(),
  projectLineItemCommandService : service('commands/project-line-item-commands'),

  session: service(),
  currentUser: readOnly('session.currentUser'),

  /**
   * @inheritDoc
   */
  propertyDefaults: {
    /**
     * @type {Application}
     */
    application: null,

    /**
     * Called when the expenses have been updated.
     *
     * @return {Function}
     */
    onExpensesUpdated: () => (function() {}),
  },

  /**
   * @inheritDoc
   */
  stateDefaults: {
    /**
     * @type {Boolean}    True while api requests are in flight to update the line items
     */
    areExpensesBeingUpdated: false,

    /**
     * The line items that are relevant for expenses.
     *
     * @type {Array<ProjectLineItem>}
     */
    currentExpenses: [],

    /**
     * These are the updated expenses.  We will not edit the actual line items.
     *
     * @type {Array<ProjectLineItem>}
     */
    updatedExpenses: [],
  },

  /**
   * @inheritDoc
   */
  initUpdatedExpenses() {
    const updatedExpenses = this.state.currentExpenses.reduce((accum, projectLineItem) => {
      const newLineItem = {
        amount: projectLineItem.amount,
        description: projectLineItem.description,
        type: projectLineItem.type,
        id: projectLineItem.id,
        isEditable: projectLineItem.isEditable,
        isDeleteable: projectLineItem.isEditable,
      };

      accum.push(newLineItem);
      return accum;
    }, []);

    this.set('state.updatedExpenses', [
      ...updatedExpenses,
      {
        amount: '',
        description: '',
        type: PROJECT_LINE_ITEM.TYPES.EXPENSE,
        isEditable: true,
        isDeleteable: false,
      },
    ]);
  },

  _isStatusCompletePublisher: computed('model.application.status', function() {
    return APPLICATION.STATUS.COMPLETE_PUBLISHER === this.model.application.status;
  }),

  _reloadApplication: task(function *(applicationId) {
    const response = yield this.applicationCommandService.loadApplication(applicationId, ['project-line-items']);
    const store = this.store;
    store.pushPayload(response);
    const application =  store.peekRecord('application', response.data.id);

    const currentExpenses = application.expenseProjectLineItems.reduce((accum, expenseProjectLineItem) => {
      const newLineItem = {
        amount: expenseProjectLineItem.amount,
        description: expenseProjectLineItem.description,
        type: expenseProjectLineItem.type,
        id: expenseProjectLineItem.id,
        isEditable: expenseProjectLineItem.isEditable,
      };

      accum.push(newLineItem);
      return accum;
    }, []);

    this.set('state.currentExpenses', currentExpenses);

    this.initUpdatedExpenses();
  }),

  /**
   * Sync the expenses that were CRUDed with the db.
   */
  _syncExpenses: task(function *() {
    let expensesToAdd = A(),
      application = this.get('model.application'),
      promises = [],
      expensesToRemove = A(),
      expensesToUpdate = A(),
      projectLineItemCommandService = this.get('projectLineItemCommandService'),
      updatedExpenses = this.get('state.updatedExpenses');

    const { currentExpenses } = this.state;

    currentExpenses.forEach(currentExpense => {
      // look for current expense in the updated expenses list.
      const foundExpense = updatedExpenses.find(updatedExpense => updatedExpense.id === currentExpense.id);
      if (!foundExpense) {
        const removeExpense = this.store.peekRecord('projectLineItem', currentExpense.id);
        // it is not there, so needs to be removed.
        expensesToRemove.addObject(removeExpense);
      }
    });

    updatedExpenses.forEach(updatedExpense => {
      if (!updatedExpense.id && updatedExpense.amount && updatedExpense.description) {
        expensesToAdd.addObject(updatedExpense);
        return;
      } else if (!updatedExpense.id) {
        // this is a new expense that didn't have an amount and description set, so it won't be added or updated.
        return;
      }

      // we have an existing expense, check to see if it's amount or description has changed.
      let currentExpense = this.store.peekRecord('projectLineItem', updatedExpense.id);
      if (currentExpense.amount !== parseFloat(updatedExpense.amount) ||
      currentExpense.description !== updatedExpense.description) {
        expensesToUpdate.addObject(updatedExpense);
      }
    });

    let wereExpensesUpdated = expensesToAdd.get('length') || expensesToUpdate.get('length') || expensesToRemove.get('length');
    if (!wereExpensesUpdated) {
      closeModal(this);
      return;
    }

    this.set('state.areExpensesBeingUpdated', true);

    if (this.get('model.application.status') === APPLICATION.STATUS.COMPLETE_PUBLISHER
      && !this.get('currentUser.isAdmin')
    ) {
      // in this case, we need to reopen the app before adding any line items
      let isError = false;
      yield this.get('applicationCommandService').withdrawCompleteRequest(this.get('model.application')).catch(() => {
        isError = true;
      });

      if (isError) {
        // this error will be show in command, so we can just abort here
        return;
      }
    }

    expensesToRemove.forEach(expense => {
      promises.push(projectLineItemCommandService.deleteProjectLineItem(application, expense));
    });

    expensesToUpdate.forEach(expense => {
      promises.push(projectLineItemCommandService.updateProjectLineItem(
        application,
        expense,
        expense.amount,
        expense.description));
    });

    expensesToAdd.forEach(expense => {
      promises.push(projectLineItemCommandService.createProjectLineItem(
        application,
        PROJECT_LINE_ITEM.TYPES.EXPENSE,
        expense.amount,
        expense.description));
    });

    let saveError = null;
    yield Promise.all(promises).catch(error => {
      saveError = error;
    });

    this.set('state.areExpensesBeingUpdated', false);

    if (!saveError) {
      this.get('model.onExpensesUpdated')();
    } else {
      handleErrorResponseWithModal(this, saveError);
    }

    closeModal(this);
  }),

   /**
   * @type {boolean}
   */
  _disableInputs: computed(
    'state.areExpensesBeingUpdated',
    '_reloadApplication.isRunning',
    function() {
      return this.state.areExpensesBeingUpdated
        || this._reloadApplication.isRunning;
    }
  ),

  /**
   * @type {Number}   The total amount of all the expenses.
   */
  _totalAmount: computed('state.updatedExpenses.@each.amount', function() {
    return this.state.updatedExpenses.reduce((carry, lineItem) => {
      let amount = parseFloat(lineItem.amount);
      if (!amount) {
        amount = 0;
      }

      return carry + amount;
    }, 0);
  }),

  init() {
    this._super(...arguments);

    this._reloadApplication.perform(this.model.application.id);
  },

  actions: {
    /**
     * Called when user clicks to delete the expense
     *
     * @param {ProjectLineItem} expense
     */
    onDeleteExpenseClick: function(expense) {
      if (!expense || this.state.areExpensesBeingUpdated) return;

      const currentUpdatedExpenses = [...this.state.updatedExpenses];

      const deleteIndex = currentUpdatedExpenses.findIndex(({ id }) => {
        return expense.id === id;
      });

      if (deleteIndex >= 0) {
        currentUpdatedExpenses.splice(deleteIndex, 1);
        this.set('state.updatedExpenses', currentUpdatedExpenses);
      }
    },

    /**
     * Called when user clicks to save the expenses.
     */
    onUpdateExpensesClicked: function() {
      if (this.get('state.areExpensesBeingUpdated')) {
        return;
      }

      this.get('_syncExpenses').perform();
    },

    onTextInputChange: function(field, index, value) {
      const errors = {};
      let editingEmpty = false;

      // Filter out all empty line items
      let updatedExpenses = this.state.updatedExpenses.reduce((accum, projectLineItem, currentIndex) => {
        if (index === currentIndex) {
          set(projectLineItem, field, value);
        }

        if ((projectLineItem.id || projectLineItem.description || projectLineItem.amount)) {
          if (!/^[0-9]{0,9}(\.[0-9]{2}){0,1}$/.test(projectLineItem.amount)) {
            errors[`amount-${index}`] = 'Invalid amount';
          }

          accum.push(projectLineItem);
        } else if (currentIndex === index) {
          // Always keep the current field to avoid focus issues
          editingEmpty = true;
          accum.push(projectLineItem);
        }
        return accum;
      }, []);

      setErrors(
        this,
        errors,
        this.state.updatedExpenses.reduce((accum, projectLineItem, currentIndex) => {
          accum.push(`amount-${currentIndex}`);
          accum.push(`description-${currentIndex}`);
          return accum;
        }, [])
      );

      // Add a final empty line if needed
      if (!editingEmpty) {
        updatedExpenses = [
          ...updatedExpenses,
          {
            amount: '',
            description: '',
            type: PROJECT_LINE_ITEM.TYPES.EXPENSE,
            isEditable: true,
            isDeleteable: false,
          },
        ];
      }

      this.set('state.updatedExpenses', updatedExpenses);
    },

    closeModal() {
      closeModal(this);
    },
  },
});
